import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { auth } from "../services/firebase";

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3070';

const AGENT_COLORS = {
  builder:   { bg: "var(--agent-builder-bg)",   text: "var(--agent-builder-text)"   },
  content:   { bg: "var(--agent-content-bg)",   text: "var(--agent-content-text)"   },
  marketing: { bg: "var(--agent-marketing-bg)", text: "var(--agent-marketing-text)" },
  research:  { bg: "var(--agent-research-bg)",  text: "var(--agent-research-text)"  },
  ceo:       { bg: "var(--surface-2)",           text: "var(--text-soft)"            },
};

// Renders **bold** and *italic* inline
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**"))
      return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*"))
      return <em key={i}>{p.slice(1, -1)}</em>;
    return p;
  });
}

export default function OnboardingStreamPage() {
  const navigate        = useNavigate();
  const [params]        = useSearchParams();
  const workspaceId     = params.get("workspace_id");
  const autoRunParam    = params.get("auto_run") === "true";

  const { } = useAuth();
  const [text, setText]                   = useState("");
  const [tasks, setTasks]                 = useState([]);
  const [done, setDone]                   = useState(false);
  const [progress, setProgress]           = useState(0);
  const [autoRun, setAutoRun]             = useState(autoRunParam);
  const [mode, setMode]                   = useState(autoRunParam ? "full" : "ceo");
  const [error, setError]                 = useState("");
  const [businessName, setBusinessName]   = useState("");
  const [alreadyDone, setAlreadyDone]     = useState(false);
  const [checking, setChecking]           = useState(true);

  const TOKEN_DELAY_MS = 32;
  const tokenBufRef = useRef([]);
  const dripRef     = useRef(null);
  const textRef     = useRef(null);
  const progRef     = useRef(null);
  const streamRef   = useRef(null);

  // Check if workspace already has summary
  useEffect(() => {
    if (!workspaceId) { setChecking(false); return; }
    apiFetch(`/api/workspaces/${workspaceId}`)
      .then(ws => {
        if (ws.workspace_summary) {
          setBusinessName(ws.name || "");
          setText(ws.workspace_summary);
          if (ws.tasks?.length) setTasks(ws.tasks.map(t => ({ ...t, _id: t.id })));
          setProgress(100);
          setDone(true);
          setAlreadyDone(true);
        }
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [workspaceId]);

  // Token drip interval
  useEffect(() => {
    dripRef.current = setInterval(() => {
      if (tokenBufRef.current.length === 0) return;
      const next = tokenBufRef.current.shift();
      setText(prev => prev + next);
      if (textRef.current) textRef.current.scrollTop = textRef.current.scrollHeight;
    }, TOKEN_DELAY_MS);
    return () => {
      clearInterval(dripRef.current);
      if (tokenBufRef.current.length > 0) {
        setText(prev => prev + tokenBufRef.current.join(""));
        tokenBufRef.current = [];
      }
    };
  }, []);

  // Progress bar animation
  useEffect(() => {
    progRef.current = setInterval(() => {
      setProgress(p => {
        if (p >= 92) { clearInterval(progRef.current); return p; }
        const step = p < 30 ? 1.4 : p < 60 ? 0.8 : 0.3;
        return Math.min(p + step, 92);
      });
    }, 600);
    return () => clearInterval(progRef.current);
  }, []);

  // SSE stream
  const startStream = useCallback(async () => {
    if (!workspaceId) { setError("Missing workspace ID."); return; }
    const ctrl = new AbortController();
    streamRef.current = ctrl;
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/api/workspaces/${workspaceId}/stream`, {
        headers, signal: ctrl.signal,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Stream error ${res.status}`);
      }

      const reader  = res.body.getReader();
      const decoder = new TextDecoder();
      let   buf     = "";

      while (true) {
        const { value, done: rdone } = await reader.read();
        if (rdone) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const dataLine = part.split("\n").find(l => l.startsWith("data: "));
          if (!dataLine) continue;
          try {
            const evt = JSON.parse(dataLine.slice(6));
            if      (evt.type === "name_generated") { if (evt.business_name) setBusinessName(evt.business_name); }
            else if (evt.type === "token")          { tokenBufRef.current.push(evt.text); }
            else if (evt.type === "task_created")   { setTasks(prev => [...prev, { ...evt.task, _id: Date.now() + Math.random() }]); }
            else if (evt.type === "done")           { handleDone(); }
            else if (evt.type === "error")          { setError(evt.message || "Stream error."); }
          } catch { /* skip malformed */ }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message || "Connection failed.");
    }
  }, [workspaceId]); // eslint-disable-line

  useEffect(() => {
    if (checking) return;
    if (alreadyDone) return;
    startStream();
    return () => streamRef.current?.abort();
  }, [startStream, alreadyDone, checking]);

  async function handleDone() {
    clearInterval(progRef.current);
    setProgress(100);
    setDone(true);
    if (autoRun) {
      try { await apiFetch(`/api/workspaces/${workspaceId}/run`, { method: "POST" }); }
      catch (e) { console.warn("Auto-run trigger failed:", e.message); }
    }
  }

  return (
    <div style={s.root}>
      {/* ── Header ── */}
      <header style={s.header}>
        <Link to="/dashboard" style={s.headerLogo}>
          raji<span style={{ color: "var(--accent)" }}>.</span>ai
        </Link>

        {businessName && <span style={s.headerName}>{businessName}</span>}

        {/* Progress bar */}
        <div style={s.progTrack}>
          <div style={{ ...s.progFill, width: `${progress}%`, ...(done ? s.progDone : {}) }} />
        </div>

        {/* Mode toggle */}
        <div style={s.modeToggle}>
          {["ceo", "full"].map(m => (
            <button
              key={m}
              style={{ ...s.modeBtn, ...(mode === m ? s.modeBtnOn : {}) }}
              onClick={() => { setMode(m); setAutoRun(m === "full"); }}
            >
              {m === "ceo" ? "CEO only" : "Full team"}
            </button>
          ))}
        </div>
      </header>

      {/* ── Body ── */}
      <div style={s.body}>

        {/* Left: streaming prose */}
        <div style={s.left}>
          <div style={s.streamLabel}>
            <span style={s.streamDot} />
            CEO Agent — Live Analysis
          </div>

          <div ref={textRef} style={s.prose}>
            {text
              ? <>
                  {text.split("\n").map((line, i) => {
                    if (line.trim().startsWith("TASK:")) return null;
                    if (line.startsWith("### ")) return <h3 key={i} style={s.proseH3}>{renderInline(line.slice(4))}</h3>;
                    if (line.startsWith("## "))  return <h2 key={i} style={s.proseH2}>{renderInline(line.slice(3))}</h2>;
                    if (line.startsWith("# "))   return <h1 key={i} style={s.proseH1}>{renderInline(line.slice(2))}</h1>;
                    if (line.match(/^[-*]\s/))   return <li key={i} style={s.proseLi}>{renderInline(line.slice(2))}</li>;
                    return <p key={i} style={s.proseLine}>{renderInline(line) || "\u00A0"}</p>;
                  })}
                  {!done && <span style={s.cursor} />}
                </>
              : <div style={s.proseEmpty}>
                  <span style={s.proseEmptyDot} />
                  <span style={{ color: "var(--text-mute)" }}>Analyzing your idea…</span>
                </div>
            }

            {error && <p style={s.error}>{error}</p>}

            {done && (
              <p style={s.doneMsg}>
                ✓ Your business plan is ready. Read through the analysis above, then head to your dashboard when you're ready.
              </p>
            )}
          </div>

          {/* Sticky footer — always visible when done */}

        </div>

        {/* Right: task cards */}
        <div style={s.right}>
        {done && (
            <div style={s.doneBlock}>
              <button
                onClick={() => { window.location.href = "/dashboard/ceo-runlog?workspace_id=" + workspaceId; }}
                style={s.doneBtn}
              >
                Go to CEO Report →
              </button>
            </div>
          )}
          <div style={s.rightLabel}>I have created the following tasks for AI employees:</div>

          {tasks.length === 0 && !done && (
            <div style={s.skeleton}>
              <div style={s.skeletonBadge} />
              <div style={s.skeletonLine} />
              <div style={{ ...s.skeletonLine, width: "70%", marginTop: 6 }} />
            </div>
          )}

<div style={s.taskList}>
  {tasks.map((task, idx) => {
    const c = AGENT_COLORS[task.agent_type] || AGENT_COLORS.ceo;

    // Construct the URL dynamically
    const taskLink = `/dashboard/tasks/${task._id}?workspace_id=${task.workspace_id}`;

    return (
      <a
        key={task._id}
        href={taskLink}
        style={{ ...s.taskCard, animationDelay: `${idx * 0.05}s`, textDecoration: 'none', color: 'inherit', display: 'block' }}
      >
        <div style={s.taskCardTop}>
          <span style={{ ...s.agentBadge, background: c.bg, color: c.text }}>
            <span style={{ ...s.agentDot, background: c.text }} />
            {task.agent_type}
          </span>
        </div>
        <p style={s.taskTitle}>{task.title}</p>
        {task.description && (
          <p style={s.taskDesc}>
            {task.description.slice(0, 100)}{task.description.length > 100 ? "…" : ""}
          </p>
        )}
      </a>
    );
  })}
</div>

          {tasks.length > 0 && (
            <p style={s.taskCount}>{tasks.length} task{tasks.length !== 1 ? "s" : ""} created</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   STYLES
   ───────────────────────────────────────────── */
const s = {
  root: {
    minHeight: "100vh",
    background: "var(--bg)",
    display: "flex",
    flexDirection: "column",
    color: "var(--text)",
    fontFamily: "var(--sans)",
  },

  // ── Header
  header: {
    height: 52,
    borderBottom: "1px solid var(--border)",
    display: "flex",
    alignItems: "center",
    padding: "0 24px",
    gap: 20,
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "var(--surface)",
  },
  headerLogo: {
    fontFamily: "var(--serif)",
    fontSize: 20,
    color: "var(--text)",
    textDecoration: "none",
    flexShrink: 0,
  },
  headerName: {
    fontFamily: "var(--serif)",
    fontSize: 15,
    color: "var(--text-soft)",
    letterSpacing: "0.01em",
    flexShrink: 0,
  },
  progTrack: {
    flex: 1,
    height: 3,
    background: "var(--border)",
    borderRadius: 2,
    overflow: "hidden",
  },
  progFill: {
    height: "100%",
    background: "var(--accent)",
    borderRadius: 2,
    transition: "width 0.6s ease",
  },
  progDone: {
    background: "var(--teal)",
    transition: "width 0.4s ease",
  },
  modeToggle: {
    display: "flex",
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    padding: 2,
    gap: 2,
    flexShrink: 0,
  },
  modeBtn: {
    fontSize: 12,
    fontWeight: 400,
    padding: "4px 12px",
    borderRadius: "calc(var(--r) - 2px)",
    border: "none",
    background: "transparent",
    color: "var(--text-mute)",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  modeBtnOn: {
    background: "var(--surface)",
    color: "var(--text)",
    fontWeight: 500,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },

  // ── Body
  body: {
    flex: 1,
    display: "grid",
    gridTemplateColumns: "1fr 340px",
    minHeight: 0,
    height: "calc(100vh - 52px)", // fill remaining viewport
  },

  // ── Left panel
  left: {
    borderRight: "1px solid var(--border)",
    padding: "32px 36px 0",
    display: "flex",
    flexDirection: "column",
    gap: 20,
    overflow: "hidden", // prose scrolls internally; footer stays pinned
  },
  streamLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "var(--text-mute)",
    flexShrink: 0,
  },
  streamDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "var(--accent)",
    animation: "pulse 1.5s ease-in-out infinite",
    flexShrink: 0,
  },
  prose: {
    flex: 1,
    overflowY: "auto",
    paddingRight: 8,
    paddingBottom: 16,
    scrollbarWidth: "none",
  },

  // Markdown prose elements
  proseH1: {
    fontFamily: "var(--serif)",
    fontSize: 20,
    fontWeight: 600,
    color: "var(--text)",
    margin: "20px 0 6px",
    lineHeight: 1.3,
  },
  proseH2: {
    fontFamily: "var(--serif)",
    fontSize: 17,
    fontWeight: 600,
    color: "var(--text)",
    margin: "16px 0 4px",
    lineHeight: 1.3,
  },
  proseH3: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text)",
    margin: "14px 0 4px",
    textTransform: "uppercase",
    letterSpacing: "0.07em",
  },
  proseLine: {
    fontFamily: "'DM Mono', 'Fira Mono', 'Courier New', monospace",
    fontSize: 14,
    lineHeight: 1.8,
    color: "var(--text-soft)",
    marginBottom: 4,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  proseLi: {
    fontFamily: "'DM Mono', 'Fira Mono', 'Courier New', monospace",
    fontSize: 14,
    lineHeight: 1.8,
    color: "var(--text-soft)",
    marginLeft: 20,
    marginBottom: 3,
    listStyle: "disc",
  },
  proseEmpty: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    paddingTop: 8,
  },
  proseEmptyDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "var(--border)",
    animation: "pulse 1.2s ease-in-out infinite",
    flexShrink: 0,
  },
  cursor: {
    display: "inline-block",
    width: 2,
    height: 16,
    background: "var(--accent)",
    marginLeft: 2,
    verticalAlign: "middle",
    animation: "blink 1s step-start infinite",
  },

  // Done state — message inside scroll, button pinned outside
  doneMsg: {
    fontFamily: "'DM Mono', 'Fira Mono', monospace",
    fontSize: 13,
    color: "var(--green)",
    lineHeight: 1.6,
    margin: "16px 0 0",
  },
  doneBlock: {
    borderTop: "1px solid var(--border)",
    padding: "14px 0 20px",
    flexShrink: 0,
  },
  doneBtn: {
    padding: "10px 20px",
    background: "var(--accent)",
    color: "#fff",
    border: "none",
    borderRadius: "var(--r)",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  },

  error: {
    fontSize: 13,
    color: "var(--red)",
    background: "rgba(192,57,43,0.06)",
    border: "1px solid rgba(192,57,43,0.18)",
    borderRadius: "var(--r)",
    padding: "10px 14px",
    marginTop: 8,
  },

  // ── Right panel
  right: {
    padding: "28px 24px",
    background: "var(--surface)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    overflowY: "auto",
  },
  rightLabel: {
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.1em",
    color: "var(--text-mute)",
    paddingBottom: 12,
    borderBottom: "1px solid var(--border)",
    flexShrink: 0,
  },

  // Skeleton
  skeleton: {
    background: "var(--surface-2)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    padding: "16px 18px",
  },
  skeletonBadge: {
    width: 72,
    height: 20,
    borderRadius: 10,
    backgroundImage: "linear-gradient(90deg, var(--shimmer-a) 25%, var(--shimmer-b) 50%, var(--shimmer-a) 75%)",
    backgroundSize: "400px 100%",
    animation: "shimmer 1.4s infinite",
    marginBottom: 12,
  },
  skeletonLine: {
    width: "90%",
    height: 13,
    borderRadius: 6,
    backgroundImage: "linear-gradient(90deg, var(--shimmer-a) 25%, var(--shimmer-b) 50%, var(--shimmer-a) 75%)",
    backgroundSize: "400px 100%",
    animation: "shimmer 1.4s infinite",
  },

  taskList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  taskCard: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    padding: "14px 16px",
    animation: "slideIn 0.35s cubic-bezier(.22,.68,0,1.2) both",
  },
  taskCardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  agentBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    fontSize: 11,
    fontWeight: 500,
    padding: "3px 8px",
    borderRadius: 100,
    letterSpacing: "0.02em",
  },
  agentDot: {
    width: 5,
    height: 5,
    borderRadius: "50%",
    flexShrink: 0,
  },
  priorityBadge: {
    fontSize: 10,
    fontWeight: 500,
    color: "var(--accent)",
    background: "var(--agent-marketing-bg)",
    padding: "2px 7px",
    borderRadius: 8,
    letterSpacing: "0.04em",
  },
  taskTitle: {
    fontSize: 13,
    fontWeight: 500,
    color: "var(--text)",
    lineHeight: 1.4,
    marginBottom: 5,
  },
  taskDesc: {
    fontSize: 12,
    color: "var(--text-mute)",
    lineHeight: 1.55,
  },
  taskCount: {
    fontSize: 11,
    color: "var(--text-mute)",
    textAlign: "center",
    marginTop: 4,
  },
};